import { LineShareAndroid } from '@qr-front/icons/line-share-android'
import { Button, buttonCls } from '@qr-front/ui/components/Button'
import { Tooltip } from '@qr-front/ui/components/Tooltip'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { qrUriAtom } from 'features/generator/generatorState'
import { useFrameFontSize } from 'features/generator/services/client'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'

import {
	primaryColorAtom,
	secondaryColorAtom,
} from '@/content-from-sections/client'
import { getContrastingNeutralColor } from '@/utils'

import { addMissingUrlProtocol } from '../../../../utils/urlUtils'
import { ShareQrDialog } from '../../components/ShareQrDialog'
import { videoButtonListAtom } from '../../contentFormSections/VideoForm/ButtonsSection/ButtonsSection'
import { shareVideoAtom } from '../../contentFormSections/VideoForm/ShareVideoForm'
import { TVideoButton } from '../../contentFormSections/VideoForm/VideoInfoFormModel'
import {
	videoDescriptionAtom,
	videoTitleAtom,
} from '../../contentFormSections/VideoForm/VideoInformationForm'
import { TQrScreenProps } from '../../QrScreen/QrScreen'
import { videoDemoContent } from './videoDemoContent'
import { VIDEO_DEMO_PRIMARY_COLOR } from './VideoScreen'

const ShareButton = ({
	small,
	previewMode,
	demoMode,
}: {
	small: boolean
	previewMode: boolean
	demoMode: boolean
}) => {
	const t = useTranslations()

	const qrUri = useAtomValue(qrUriAtom)
	const [showShareDialog, setShowShareDialog] = useState(false)

	const secondaryColor = useAtomValue(secondaryColorAtom)

	const buttonRef = useRef<HTMLButtonElement>(null)

	return (
		<>
			<ShareQrDialog
				isOpen={showShareDialog}
				setIsOpen={setShowShareDialog}
				previewMode={previewMode}
			/>
			{!qrUri ? (
				<Tooltip anchorRef={buttonRef}>
					{t(
						'generator__content_form_section__images_information__share_button_tooltip',
					)}
				</Tooltip>
			) : null}
			<Button
				ref={buttonRef}
				visual="secondary"
				size={small ? 'icon' : 'default'}
				onClick={() => {
					if (qrUri) {
						setShowShareDialog(true)
					}
				}}
				className={cn(
					'shrink-0 bg-transparent hover:bg-transparent',
					small ? 'w-10 p-0' : 'w-full',
					'rounded-6',
				)}
				style={{
					borderColor: demoMode ? '#FFFFFF' : secondaryColor,
					color: demoMode ? '#FFFFFF' : secondaryColor,
					...{ '--tw-ring-color': demoMode ? '#FFFFFF' : secondaryColor },
				}}
			>
				<LineShareAndroid className="size-4" />
			</Button>
		</>
	)
}

const CustomButton = ({
	button,
	demoMode,
	previewMode,
	size,
}: {
	button: TVideoButton
	demoMode: boolean
	previewMode: boolean
	size: 'small' | 'large'
}) => {
	const secondaryColor = useAtomValue(secondaryColorAtom)
	const contrastingColor = getContrastingNeutralColor(secondaryColor)
	const title = button.title

	const fontSize = useFrameFontSize({
		text: title,
		maxWidth: size === 'small' ? 172 : 200,
		maxSize: previewMode || demoMode ? 12 : 15,
		fontFamily: 'var(--font-body)',
	})

	const baseClass = cn(
		buttonCls({ visual: 'secondary' }),
		'grow text-body-caption font-normal ring-transparent',
		'rounded-6',
	)

	const clone1Class = cn(
		buttonCls({ visual: 'secondary' }),
		'grow rounded-6 text-body-caption font-normal ring-transparent',
	)

	const className = getVersionForClone({
		base: baseClass,
		clone1: clone1Class,
	})

	const style = {
		backgroundColor: demoMode ? '#FFFFFF' : secondaryColor,
		color: demoMode ? '#000000' : contrastingColor,
		fontSize: `${fontSize}px`,
	}

	if (!button.url) {
		return (
			<span className={cn(className, 'cursor-not-allowed')} style={style}>
				{title}
			</span>
		)
	}

	return (
		<a
			href={addMissingUrlProtocol(button.url)}
			target="_blank"
			rel="noopener noreferrer"
			className={className}
			style={style}
		>
			{title}
		</a>
	)
}

export const VideosScreenHeader = ({
	previewMode,
	demoMode,
}: TQrScreenProps) => {
	const t = useTranslations()

	const primaryColor = useAtomValue(primaryColorAtom)
	const primaryColorContrastingColor = getContrastingNeutralColor(primaryColor)
	const showShareButton = useAtomValue(shareVideoAtom)
	const videoTitle = useAtomValue(videoTitleAtom)
	const videoDescription = useAtomValue(videoDescriptionAtom)
	const videoButtonList = useAtomValue(videoButtonListAtom)

	const demoContent = videoDemoContent(t)

	const bgHeight = `${120 / 16}rem`

	const bleedingBottomBackground = (
		<div
			className="-mt-4 w-[calc(100%+40px)] -translate-x-5 bg-red-50"
			style={{
				backgroundColor: demoMode ? VIDEO_DEMO_PRIMARY_COLOR : primaryColor,
				height: bgHeight,
				marginBottom: `-119px`,
			}}
		/>
	)

	return (
		<div
			className={cn(
				'flex flex-col gap-4 px-5 pt-8 transition-colors',
				previewMode && 'pt-6',
			)}
			style={{
				backgroundColor: demoMode ? VIDEO_DEMO_PRIMARY_COLOR : primaryColor,
			}}
		>
			<div className="flex flex-col word-break-fix">
				<h4
					className="type-heading-4 break-words text-center transition-colors"
					style={{ color: primaryColorContrastingColor }}
				>
					{demoMode ? demoContent.headline : videoTitle}
				</h4>
				<p
					className="type-10 mx-auto break-words text-center leading-[16px] text-white"
					style={{ color: primaryColorContrastingColor }}
				>
					{demoMode ? demoContent.description : videoDescription}
				</p>
			</div>
			<div className="flex justify-center">
				<div className="flex w-[220px] items-start gap-2">
					{!!(videoButtonList.length || demoMode) && (
						<div className="flex min-w-[172px] flex-1 flex-col gap-1">
							{(demoMode ? demoContent.buttons : videoButtonList).map(
								(button, index) => (
									<CustomButton
										key={index}
										button={button}
										demoMode={demoMode}
										previewMode={previewMode}
										size={
											index === 0 && (showShareButton || demoMode)
												? 'small'
												: 'large'
										}
									/>
								),
							)}
						</div>
					)}
					{showShareButton || demoMode ? (
						<ShareButton
							small={!!(videoButtonList.length || demoMode)}
							previewMode={previewMode}
							demoMode={demoMode}
						/>
					) : null}
				</div>
			</div>
			{bleedingBottomBackground}
		</div>
	)
}
